import { useCallback, useEffect, useState } from 'react';
import { employeeApi, positionApi, scheduleApi } from '../services/api';
import type { TimecardReportCommonFilters, TimecardReportStatusFilter } from '../utils/timecardReportFilters';

export function useTimecardReportFilters() {
  const [statusFilter, setStatusFilter] = useState<TimecardReportStatusFilter>('all');
  const [selectedEmployeeIds, setSelectedEmployeeIds] = useState<number[]>([]);
  const [selectedScheduleIds, setSelectedScheduleIds] = useState<number[]>([]);
  const [selectedPositionIds, setSelectedPositionIds] = useState<number[]>([]);

  const [employees, setEmployees] = useState<{ id: number; label: string }[]>([]);
  const [schedules, setSchedules] = useState<{ id: number; label: string }[]>([]);
  const [positions, setPositions] = useState<{ id: number; label: string }[]>([]);
  const [loadingOptions, setLoadingOptions] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingOptions(true);
      try {
        const [empRows, scheduleRows, positionRows] = await Promise.all([
          employeeApi.getPicker(),
          scheduleApi.getAll(),
          positionApi.getAll(),
        ]);
        if (cancelled) return;
        setEmployees(
          empRows.map((e) => ({
            id: e.id,
            label: `${e.firstName} ${e.lastName}`.trim(),
          }))
        );
        setSchedules(
          scheduleRows.filter((s) => s.isActive).map((s) => ({ id: s.id, label: s.name }))
        );
        setPositions(
          positionRows.filter((p) => p.isActive).map((p) => ({ id: p.id, label: p.title }))
        );
      } catch (e) {
        console.error('Failed to load timecard report filter options', e);
      } finally {
        if (!cancelled) setLoadingOptions(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const buildCommonFilters = useCallback((): TimecardReportCommonFilters => {
    const employeeLabels = selectedEmployeeIds.map(
      (id) => employees.find((e) => e.id === id)?.label ?? `Employee ${id}`
    );
    const scheduleLabels = selectedScheduleIds.map(
      (id) => schedules.find((s) => s.id === id)?.label ?? `Schedule ${id}`
    );
    const positionLabels = selectedPositionIds.map(
      (id) => positions.find((p) => p.id === id)?.label ?? `Position ${id}`
    );

    return {
      employeeIds: selectedEmployeeIds,
      scheduleIds: selectedScheduleIds,
      positionIds: selectedPositionIds,
      statusFilter,
      employeeLabels,
      scheduleLabels,
      positionLabels,
    };
  }, [
    selectedEmployeeIds,
    selectedScheduleIds,
    selectedPositionIds,
    statusFilter,
    employees,
    schedules,
    positions,
  ]);

  return {
    statusFilter,
    setStatusFilter,
    selectedEmployeeIds,
    setSelectedEmployeeIds,
    selectedScheduleIds,
    setSelectedScheduleIds,
    selectedPositionIds,
    setSelectedPositionIds,
    employees,
    schedules,
    positions,
    loadingOptions,
    buildCommonFilters,
  };
}
